/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactElement } from "react";
import { DraggableProvided } from "react-beautiful-dnd";
import { When } from "react-if";
import {
  Button,
  ExtendedTooltip,
  FlexContainer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { PlanActionType } from "~/generated/graphql";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { isNotNil } from "~/tools";
import { SvgName } from "~/tools/types";
import { TooltipColor } from "~/types/TooltipColor";
import { ItemCheckbox } from "./CheckboxItem";
import {
  ButtonText,
  ColorLabel,
  IconWrapper,
  ToggleButtonItem,
  Wrap,
} from "./styles";

export type ToggleItem = {
  id: string;
  icon?: SvgName;
  iconPathColors?: Record<number, string>;
  tooltip?: string;
  tooltipColor?: TooltipColor;
  iconColor?: string;
  color: string;
  name: string | JSX.Element;
  description?: string;
  actionType?: PlanActionType;
  onMouseDown?: () => void;
  onDelete?: () => void;
};

export type ToggleItemBoxProps = {
  item: ToggleItem;
  provided?: DraggableProvided;
  isSelected?: boolean;
  isCheckbox?: boolean;
  active?: boolean;
  disabled?: boolean;
  tag?: ReactElement;
};

export function ToggleItemBox({
  item,
  provided,
  isSelected,
  isCheckbox,
  active = false,
  disabled = false,
  tag,
}: ToggleItemBoxProps) {
  const textColor = disabled
    ? theme.color.text.text03
    : theme.color.text.text01;
  return (
    <Wrap ref={provided?.innerRef} {...provided?.draggableProps}>
      <ToggleButtonItem
        color={item.color}
        active={active}
        onMouseDown={(e) => {
          item.onMouseDown?.();
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <ColorLabel color={item.color} />
        <When condition={isNotNil(item.icon)}>
          <IconWrapper {...provided?.dragHandleProps}>
            <ExtendedTooltip
              caption={item?.tooltip}
              status={item?.tooltipColor}
              position="start"
            >
              <StyledSVGIcon
                name={item.icon as SvgName}
                size="md"
                color={item.iconColor}
                pathColors={item.iconPathColors}
              />
            </ExtendedTooltip>
          </IconWrapper>
        </When>
        <ItemCheckbox
          isSelected={isSelected}
          isVisible={isCheckbox}
          disabled={disabled}
        />
        <ButtonText css={{ width: "100%" }}>
          <FlexContainer
            direction="column"
            alignItems="flex-start"
            mr={theme.spacing.spacing02}
            css={{ width: "100%", minWidth: 0 }}
          >
            <Text
              tag="div"
              align="left"
              mb={0}
              weight="medium"
              width="100%"
              color={textColor}
            >
              {item.name}
            </Text>
            <When condition={item.description != null}>
              <Text
                tag="div"
                mb={0}
                align="left"
                mt={theme.spacing.spacing01}
                size="sm"
                marginTop={0}
                color={textColor}
              >
                {item.description}
              </Text>
            </When>
          </FlexContainer>
        </ButtonText>
        <When condition={!!tag}>{tag}</When>
        <When condition={item.onDelete != null}>
          <Button
            ml="auto"
            icon="trash"
            severity="low"
            size="md"
            onClick={item.onDelete}
          />
        </When>
      </ToggleButtonItem>
    </Wrap>
  );
}
